import type { IPersonStatus } from '~/utils/interface/map';
import i18n from '~/utils/i18n';

export const pickPersonStatusLabel = (status: IPersonStatus, language = i18n.language): string => {
  const labelEn = typeof status.label_en === 'string' ? status.label_en.trim() : '';
  return language.toLowerCase().startsWith('en') && labelEn ? labelEn : status.label;
};

const normalizeHexColor = (color: string): string | null => {
  const normalized = color.trim().replace('#', '');
  if (!/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(normalized)) return null;
  const hex =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;
  return `#${hex.toUpperCase()}`;
};

const hexToRgb = (hexColor: string): { r: number; g: number; b: number } | null => {
  const normalized = normalizeHexColor(hexColor);
  if (!normalized) return null;

  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  };
};

export const getStatusLabel = (
  value: number | null,
  statusList: IPersonStatus[] = [],
  unknownLabel = '',
  language = i18n.language
): string => {
  if (value === null) return unknownLabel;
  const status = statusList.find((s) => s.value === value);
  if (!status) return unknownLabel;
  return pickPersonStatusLabel(status, language);
};

export const getStatusColor = (
  value: number | null,
  statusList: IPersonStatus[] = []
): string | null => {
  if (value === null) return null;
  const rawColor = statusList.find((s) => s.value === value)?.color;
  if (typeof rawColor !== 'string' || !rawColor.trim()) return null;
  return normalizeHexColor(rawColor);
};

const toRgbaFromHex = (hexColor: string, opacity: number): string => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return 'transparent';
  const { r, g, b } = rgb;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const getStatusActiveBackgroundColor = (
  value: number | null,
  statusList: IPersonStatus[] = []
): string | undefined => {
  const statusColor = getStatusColor(value, statusList);
  if (!statusColor) return undefined;
  return toRgbaFromHex(statusColor, 0.1);
};

/** Màu status từ BE kết hợp opacity (vd. nền icon khi marker bị dim). */
export const getStatusColorWithOpacity = (
  value: number | null,
  statusList: IPersonStatus[],
  opacity: number
): string | null => {
  const statusColor = getStatusColor(value, statusList);
  if (!statusColor) return null;
  return toRgbaFromHex(statusColor, opacity);
};

export const getStatusStyleVars = (
  value: number | null,
  statusList: IPersonStatus[] = []
): Record<string, string> => {
  const statusColor = getStatusColor(value, statusList);
  if (!statusColor) return {};

  return {
    '--status-label-bg': toRgbaFromHex(statusColor, 0.12),
    '--status-label-text': statusColor,
    '--status-marker-color': statusColor,
    '--status-marker-border': toRgbaFromHex(statusColor, 0.32),
    '--status-marker-shadow': `0 10px 24px ${toRgbaFromHex(statusColor, 0.24)}`,
  };
};
